/**
 * Adzuki. My Settings - save UX
 *
 * SET-12, with SET-74 (status indicator), SET-75 (Save Changes button) and SET-76 (the
 * discard-confirmation dialog).
 *
 * DIRTY IS A VALUE COMPARISON AGAINST ENTRY STATE (S44), NOT A "SOMETHING CHANGED" FLAG.
 * A field edited and put back is not dirty. That is what makes ground rule 1 literally true -
 * Save is disabled exactly when there is nothing to save - and it is why the discard dialog
 * stops firing for someone who typed a character and deleted it.
 *
 * THE COMPARISON IS PER CONTROL TYPE, AND A SINGLE DEEP-EQUALITY HELPER IS WRONG TWICE, IN
 * OPPOSITE DIRECTIONS:
 *
 *   - a tag list compared order-sensitively reports dirty when the advisor only re-ordered a
 *     set whose order carries no meaning;
 *   - the shortcuts list compared as a set reports clean when re-ordering is the entire edit.
 *
 * So scalars compare trimmed, sets compare as sets, and order-sensitive lists opt in. See
 * _comparable().
 *
 * NOTHING HERE HAND-ROLLS A SUBMIT (S15). The Save button triggers the panel form's own submit
 * and js/Adzuki/form.js does the rest - validation, the attribute map, the POST. This module
 * only decides when the button is live, what the indicator says, and whether leaving is safe.
 */

(function MySettingsSave($, Adzuki)
{
	"use strict";

	// how long "Saved" shows before falling back to the rest state
	var SAVED_MS = 2000;

	var FIELDS = 'input, select, textarea';

	var save        = {},
	    entry       = null,
	    status      = 'blank',
	    saved_timer = null,
	    deferred    = null,
	    saved_url   = null,
	    prompt_fields = [],
	    prompt_action = null,
	    // SET-43: the sentence shown by the 'partial' state, set by whoever reports the partial
	    // failure because only they know what half-failed. Cleared with every other state.
	    partial_text  = null;

	var _panelForm,
	    _cleanState,
	    _comparable,
	    _snapshot,
	    _isDirty,
	    _differsFromErrors,
	    _revealErrorPane,
	    _paneFromResponse,
	    _openPrompt,
	    _closePrompt,
	    _setStatus,
	    _paint,
	    _recompute,
	    _openDialog,
	    _closeDialog,
	    _resume;

	/**
	 * The form in the open panel. There is exactly one - a save covers one form class (S32) -
	 * and it is found by the framework's own data-form-component marker rather than by name.
	 */
	_panelForm = function panelForm()
	{
		var $overlay = $('.my-settings-overlay');

		if ($overlay.length === 0) {
			return $();
		}

		return $overlay.find('form[data-form-component]').first();
	};

	/**
	 * One control reduced to a string that can be compared with ===.
	 *
	 * The default comes from the control type. Two overrides are declared on the element
	 * itself, because the rule belongs to the control rather than to a list of field names
	 * maintained somewhere else:
	 *
	 *   data-my-settings-compare="set"      compare as a set - order is not the edit
	 *   data-my-settings-compare="ordered"  compare in order - re-ordering IS the edit
	 *
	 * The shortcuts list is the case that needs "ordered", and declares it when that panel is
	 * built. A multi-select is already a set by default, so multi-selects and tag lists need
	 * no attribute.
	 */
	_comparable = function comparable($el)
	{
		var el     = $el[0],
		    mode   = $el.attr('data-my-settings-compare'),
		    id     = $el.attr('id'),
		    values = [],
		    editor;

		if (el.type === 'checkbox') {
			return (el.checked ? '1' : '0');
		}

		if (el.type === 'radio') {
			// only the checked member of the group says anything
			return (el.checked ? String(el.value) : '');
		}

		// an htmlEditor field is a textarea tinymce has taken over, so the textarea's own value
		// goes stale the moment the advisor types in the iframe
		if (id && window.tinymce && tinymce.get(id)) {
			editor = tinymce.get(id);

			return $.trim(editor.getContent());
		}

		if (el.multiple || mode === 'set' || mode === 'ordered') {
			values = ($el.val() || []);

			if (!$.isArray(values)) {
				values = [values];
			}

			values = $.map(values, function asString(v) {
				return String(v);
			});

			// a set has no order to preserve; an ordered list is compared as it stands
			if (mode !== 'ordered') {
				values.sort();
			}

			// a separator that cannot occur in a value - joining on '' would make ['ab','c']
			// and ['a','bc'] compare equal
			return values.join('\u0001');
		}

		// scalars compare TRIMMED. The server runs _trimFormData(), so a whitespace-only
		// difference would not survive the save - reporting it dirty would offer to save
		// nothing at all.
		return $.trim(String($el.val() === null ? '' : $el.val()));
	};

	/**
	 * @return object field name -> comparable value
	 */
	_snapshot = function snapshot($form)
	{
		var shot = {};

		if ($form.length === 0) {
			return shot;
		}

		$form.find(FIELDS).each(function eachField() {
			var $el  = $(this),
			    name = $el.attr('name');

			// controls the advisor cannot edit are not part of the comparison: a readonly field
			// cannot be changed and a disabled one does not even post
			if (!name || this.disabled || $el.prop('readonly')) {
				return;
			}

			// radios share a name, so the checked one has to win rather than the last one seen
			if (this.type === 'radio') {
				if (this.checked) {
					shot[name] = _comparable($el);
				} else if (!(name in shot)) {
					shot[name] = '';
				}

				return;
			}

			// A REPEATED NAME IS A LIST, NOT LAST-ONE-WINS. A `name[]` group puts one element per
			// value under a single name - the specialties rows write three such arrays, and Maxx
			// Assist's travel types are six checkboxes sharing one - so ASSIGNING here would keep
			// only the final element and report every change to the others as clean. That is SET-161:
			// a reorder that left the last row alone, and featuring any row but the last, both looked
			// like nothing had happened and Save stayed disabled.
			//
			// APPENDING KEEPS THE GROUP IN ORDER, which is what makes a reorder a change at all.
			//
			// THE SEPARATOR IS _comparable()'s, for its reason: it cannot occur in a value, so
			// ['ab','c'] and ['a','bc'] do not compare equal.
			//
			// SAFE FOR THE HIDDEN COMPANION DefaultForm::checkbox() emits. That hidden `0` shares the
			// checkbox's name, so the pair now reads "0\u00011" rather than "1" - stable, and still
			// different when the box is toggled. Radios keep their own branch above, where the CHECKED
			// member wins rather than the last one seen.
			shot[name] = ((name in shot) ? shot[name] + '\u0001' : '') + _comparable($el);
		});

		return shot;
	};

	/**
	 * What the indicator says when there is nothing to save.
	 *
	 * BLANK ON ARRIVAL, NOT "All changes saved". Saying that the moment the modal opens
	 * claims something was just saved when the advisor has done nothing at all - so the
	 * indicator stays silent until it has something true to report. "All changes saved" is
	 * the settle state AFTER a save, which is when it is accurate.
	 *
	 * This is also what the prototype does: it renders the status only while dirty or just
	 * saved. It does mean SET-12's AC 1 and SET-74's rest state need rewording - reported on
	 * the ticket rather than left as a quietly failed criterion.
	 */
	_cleanState = function cleanState()
	{
		var url = ((Adzuki.MySettings && typeof Adzuki.MySettings.currentUrl === 'function')
			? Adzuki.MySettings.currentUrl()
			: null);

		return ((saved_url !== null && saved_url === url) ? 'rest' : 'blank');
	};

	_isDirty = function isDirty()
	{
		var now = _snapshot(_panelForm()),
		    key;

		if (entry === null) {
			return false;
		}

		for (key in now) {
			if (Object.prototype.hasOwnProperty.call(now, key) && now[key] !== entry[key]) {
				return true;
			}
		}

		// a control that has gone away since entry counts as a change too
		for (key in entry) {
			if (Object.prototype.hasOwnProperty.call(entry, key) && !(key in now)) {
				return true;
			}
		}

		return false;
	};

	/**
	 * SET-123: brings the pane holding a rejected field into view.
	 *
	 * A save covers the whole form group, so a field on a pane the advisor is not looking at
	 * can fail validation - and Franchise Profile guarantees it will happen, because
	 * postcard_from_address is required on Home Office Marketing while four more required
	 * fields sit on Details. Without this the save is refused, the message names a field, and
	 * the field is nowhere on screen: the cross-section hunting S16 exists to prevent, arriving
	 * inside one form.
	 *
	 * form.js has already marked the fields and tried to focus the first one; its fallback
	 * looks for a VISIBLE input and finds none, so the focus is re-taken here after the pane is
	 * revealed.
	 */
	/**
	 * SET-16 AC 7: fields that may not equal a value the browser already holds.
	 *
	 * Declared by the panel as data-my-settings-differs-from (the value to compare against) and
	 * data-my-settings-differs-message (what to say), so this knows the RULE without knowing the
	 * field - My Profile's alternate email is the only one today and the next needs no JS.
	 *
	 * MIRRORS THE SERVER COMPARISON EXACTLY, INCLUDING ITS LOOSENESS. AgentsEditForm lowercases
	 * both sides and does NOT trim, so neither does this. Trimming here would block a value the
	 * server would have accepted, which is the client inventing a rule rather than mirroring one.
	 *
	 * AN EMPTY FIELD IS NOT A COLLISION. The server never sees one as equal to a real address, so
	 * skipping it keeps the two in step rather than being a kindness.
	 *
	 * @param  object $form jQuery form.
	 *
	 * @return array form.js-shaped validation entries, empty when nothing collides.
	 */
	_differsFromErrors = function differsFromErrors($form)
	{
		var out = [];

		$form.find('[data-my-settings-differs-from]').each(function checkField() {
			var $field = $(this),
			    other  = $field.attr('data-my-settings-differs-from') || '',
			    value  = $field.val() || '',
			    message;

			if (other === '' || value === '') {
				return;
			}

			if (value.toLowerCase() !== other.toLowerCase()) {
				return;
			}

			message = $field.attr('data-my-settings-differs-message') || 'That value cannot match the one it is checked against.';

			out.push({
				attributes: {
					id  : $field.attr('id'),
					name: $field.attr('name')
				},
				errors: [message]
			});
		});

		return out;
	};

	/**
	 * SET-168: the pane a FORM-LEVEL error named, when no field was marked to find it by.
	 *
	 * Read from the validation entries the response already carries - DefaultForm puts it there
	 * when setValidationError() is given a pane. Returns the first one, because a save that
	 * produced several form-level errors can only reveal one pane and the first is the one whose
	 * message leads.
	 */
	_paneFromResponse = function paneFromResponse(response)
	{
		var data = ((response && response.data) ? response.data : null),
		    i;

		if (!data) {
			return null;
		}

		for (i in data) {
			if (Object.prototype.hasOwnProperty.call(data, i) && data[i] && data[i].pane) {
				return data[i].pane;
			}
		}

		return null;
	};

	_revealErrorPane = function revealErrorPane(response)
	{
		var $errored = $('.my-settings-overlay .my-settings-pane:not(.is-current)')
			.find('.has-error')
			.first(),
		    $pane,
		    key,
		    $focus;

		// SET-168: NO MARKED FIELD, SO ASK THE ERROR WHERE IT BELONGS.
		//
		// Everything below locates a pane by the field inside it. A form-level error marks no
		// field - deliberately, where a rule fails a group of fields as a unit - so there is
		// nothing to search for and the advisor was left on the pane they were already on,
		// reading a message about a tab they could not see.
		//
		// NOTHING IS FOCUSED ON THIS PATH, and that is right rather than a shortcut: there is no
		// errored field to put the cursor in, and focusing the pane's first input would point at
		// a field that may be perfectly valid.
		if ($errored.length === 0) {
			key = _paneFromResponse(response);

			if (!key || !Adzuki.MySettings || typeof Adzuki.MySettings.showPane !== 'function') {
				return;
			}

			// THE PANE HAS TO BE HERE AND NOT ALREADY SHOWING. A hint naming a pane outside this
			// form group - or one the advisor is already looking at - degrades to doing nothing,
			// which is what the surface did before this existed.
			$pane = $('.my-settings-overlay .my-settings-pane:not(.is-current)')
				.filter('[data-pane="' + key + '"]');

			if ($pane.length === 0) {
				return;
			}

			Adzuki.MySettings.showPane(key);

			return;
		}

		$pane = $errored.closest('.my-settings-pane');
		key   = $pane.attr('data-pane');

		if (!key || !Adzuki.MySettings || typeof Adzuki.MySettings.showPane !== 'function') {
			return;
		}

		Adzuki.MySettings.showPane(key);

		$focus = $pane.find('.has-error').first().find(FIELDS).filter(':visible').first();

		if ($focus.length) {
			$focus.focus();
		}
	};

	// ------------------------------------------------------------------
	// SET-74: the five states
	// ------------------------------------------------------------------

	_setStatus = function setStatus(next)
	{
		if (saved_timer !== null) {
			window.clearTimeout(saved_timer);

			saved_timer = null;
		}

		status = next;

		// SET-43: the partial sentence belongs to one state and must not outlive it, or the next
		// panel's partial failure inherits the last one's wording.
		if (next !== 'partial') {
			partial_text = null;
		}

		if (next === 'saved') {
			saved_timer = window.setTimeout(function backToRest() {
				saved_timer = null;

				// only fall back if nothing has been touched since
				if (status === 'saved') {
					_setStatus(_isDirty() ? 'dirty' : _cleanState());
				}
			}, SAVED_MS);
		}

		_paint();
	};

	/**
	 * Paints the indicator and the button from the current state.
	 *
	 * Called on every panel render as well as every state change, because the topbar is part
	 * of the server-rendered fragment - so the panel re-fetch that follows a save would
	 * otherwise wipe "Saved" off the screen the instant it appeared.
	 */
	_paint = function paint()
	{
		var $status = $('.my-settings-status'),
		    $button = $('.my-settings-save'),
		    states  = {
			blank: {
				text: '',
				icon: '',
				cls : ''
			},
			rest: {
				text: 'All changes saved',
				icon: 'fa-check',
				cls : ''
			},
			dirty: {
				text: 'You have unsaved changes',
				icon: 'fa-exclamation-circle',
				cls : 'is-dirty'
			},
			saving: {
				text: 'Saving\u2026',
				icon: 'fa-circle-notch fa-spin',
				cls : 'is-saving'
			},
			saved: {
				text: 'Saved',
				icon: 'fa-check',
				cls : 'is-saved'
			},
			failed: {
				text: 'Save failed \u2014 retry',
				icon: 'fa-exclamation-circle',
				cls : 'is-failed'
			},
			// SET-43: THE SAVE WORKED AND SOMETHING IT TRIGGERED DID NOT.
			//
			// The settings themselves are stored; what partly failed is a side effect that reaches
			// outside this application - one external call per future air reservation. Reporting
			// that as 'saved' would be false, and as 'failed' would be worse, because it would send
			// the advisor back to re-enter settings that are already correct.
			//
			// THE TEXT IS SET BY THE CALLER, since only the form knows what half-failed. This entry
			// carries the styling and a fallback for the case where no message came back.
			partial: {
				text: (partial_text || 'Saved \u2014 some changes could not be applied'),
				icon: 'fa-exclamation-circle',
				cls : 'is-partial'
			}
		    },
		    state = (states[status] || states.blank),
		    enabled;

		if ($status.length) {
			$status.removeClass('is-dirty is-saving is-saved is-failed is-partial').addClass(state.cls);

			if (state.text === '') {
				$status.empty();
			} else {
				$status.html('<i class="fas ' + state.icon + '" aria-hidden="true"></i> ' + state.text);
			}
		}

		if ($button.length) {
			// enabled when there is something to save, and after a failure so a retry does not
			// require touching a field first
			enabled = (status === 'dirty' || status === 'failed');

			// gray while there is nothing to save, blue once there is - so the button reads as
			// inert rather than as a primary action waiting to be pressed
			$button
				.prop('disabled', !enabled)
				.toggleClass('btn-primary', enabled)
				.toggleClass('btn-default', !enabled);
		}
	};

	_recompute = function recompute()
	{
		var dirty;

		// a request in flight owns the indicator; do not talk over it
		if (status === 'saving') {
			return;
		}

		dirty = _isDirty();

		if (dirty && status !== 'dirty') {
			// SET-58: EDITING SPENDS THE "All changes saved" CLAIM.
			//
			// That message describes the save that just happened. Once the advisor has edited
			// anything since, it no longer describes the form - so a field typed and then put
			// back should leave the indicator SILENT rather than reasserting a save that is now
			// two edits old. Reverting a field is not saving it.
			//
			// Clearing the memo is all it takes: _cleanState() falls back to blank without one,
			// and save.succeeded() sets it again on the next real save.
			saved_url = null;

			_setStatus('dirty');
		} else if (!dirty && (status === 'dirty' || status === 'failed')) {
			_setStatus(_cleanState());
		}

		// SET-12's per-tab modified marker used to be painted from here. It was removed on
		// 8 Sep 2026 - see _my_settings.scss - so the dirty state is reported once, by the
		// top-bar indicator above.
	};

	// ------------------------------------------------------------------
	// SET-76: the discard dialog
	// ------------------------------------------------------------------

	/**
	 * @param object action What to do if the advisor chooses to leave.
	 */
	_openDialog = function openDialog(action)
	{
		deferred = action;

		$('.my-settings-guard').prop('hidden', false).find('.my-settings-confirm-keep').focus();
	};

	_closeDialog = function closeDialog()
	{
		$('.my-settings-guard').prop('hidden', true);
	};

	/**
	 * Completes the action the dialog interrupted.
	 *
	 * NOTHING IS RESTORED ON DISCARD, AND THAT IS CORRECT RATHER THAN LAZY. Both triggers -
	 * closing, and switching subsection - leave the panel, so the edits go with it and the
	 * next render fetches last-saved state from the server. There is no path where the advisor
	 * discards and stays on the same fields.
	 */
	_resume = function resume()
	{
		var action = deferred;

		deferred = null;
		entry    = null;

		_closeDialog();

		if (action === null) {
			return;
		}

		if (action.source === 'nav' && action.url) {
			Adzuki.MySettings.load(action.url);

			return;
		}

		Adzuki.MySettings.close();
	};

	// ------------------------------------------------------------------
	// SET-58, decision S23: the server answering Save with a question
	// ------------------------------------------------------------------

	/**
	 * Shows a form's confirmation prompt inside the modal.
	 *
	 * ONE DIALOG FOR ANY FORM. The prompt itself says which hidden field to set on confirm, so
	 * nothing here knows anything about AMEX tokens or whatever the next confirm() guards - a
	 * per-form handler would have to be written again every time a form grows one.
	 *
	 * THE BUTTON COMES OUT OF "Saving...", which INVARIANTS requires and which is also just
	 * true: nothing was saved, the form is still dirty, and the advisor may well cancel. Leaving
	 * it spinning behind a dialog claims a request is in flight when the server has already
	 * answered.
	 *
	 * @param  object response The confirmation_prompt response from form.js.
	 *
	 * @return void
	 */
	_openPrompt = function openPrompt(response, on_confirm)
	{
		var data    = ((response && response.data) ? response.data : {}),
		    message = (data.message ? data.message : 'Please confirm this change.'),
		    $prompt = $('.my-settings-prompt');

		prompt_fields = (data.fields ? data.fields : []);
		// SET-60: set for an action endpoint, null for a form. Confirming runs one or the other,
		// never both.
		prompt_action = (typeof on_confirm === 'function' ? on_confirm : null);

		// TEXT, NOT HTML. The message is server copy rather than markup, and html() here would
		// make every future confirm() message an injection surface for whatever built it.
		$prompt.find('.my-settings-prompt-text').text(message);

		// SET-26: A FORM MAY NAME ITS OWN BUTTONS, and one needs to - S42's save signs the
		// advisor out, which "Yes, continue" does not say. Reset to the defaults every time,
		// because the dialog is shared and a label left behind would describe the wrong
		// consequence on the next form that raises it.
		$prompt.find('.my-settings-prompt-continue').text(data.confirm_label ? data.confirm_label : 'Yes, continue');
		$prompt.find('.my-settings-prompt-cancel').text(data.cancel_label ? data.cancel_label : 'Cancel');

		_setStatus(_isDirty() ? 'dirty' : _cleanState());

		$prompt.prop('hidden', false).find('.my-settings-prompt-cancel').focus();
	};

	_closePrompt = function closePrompt()
	{
		prompt_fields = [];
		prompt_action = null;

		$('.my-settings-prompt').prop('hidden', true);
	};

	// ------------------------------------------------------------------
	// Public surface
	// ------------------------------------------------------------------

	/**
	 * Submits the panel form. form.js owns validation, the attribute map and the POST (S15).
	 */
	save.submit = function submit()
	{
		var $form = _panelForm(),
		    collisions;

		if ($form.length === 0) {
			return;
		}

		// SET-16 AC 7: a rule the browser can answer, answered in the browser.
		//
		// BEFORE _setStatus('saving'), because nothing is being saved - setting the status first
		// and bailing would leave the top bar claiming a save that never started.
		//
		// THE SERVER RULE STAYS AND IS STILL THE ONE THAT DECIDES (S24). This moves where the
		// advisor is told, not whether they are stopped - a payload that reaches the server with
		// a collision is still rejected there.
		collisions = _differsFromErrors($form);

		if (collisions.length > 0) {
			// form.js's OWN handlers, not a lookalike - so the popup, the .has-error marking and
			// the focus are the same ones a server rejection produces, because they are the same
			// code. A second treatment here would be a second thing to keep in step.
			Adzuki.Form.throwSubmissionError(Adzuki.Form.handleValidationErrors(collisions));

			return;
		}

		_setStatus('saving');

		$form.submit();
	};

	/**
	 * SET-60: raises the in-modal confirmation for something that is NOT a form submit.
	 *
	 * The Shipping list's delete is an action endpoint rather than a form, and it answers an
	 * unconfirmed request with the same confirmation_prompt shape a form's confirm() returns.
	 * This is how it reaches the one dialog: ground rule 6 wants a single confirmation surface
	 * inside the modal, not a second dialog per panel.
	 *
	 * @param  string   message   Copy from the server. The caller does not write it.
	 * @param  function on_confirm Run when the advisor confirms. Nothing runs on cancel.
	 *
	 * @return void
	 */
	save.confirm = function confirmPublic(message, on_confirm)
	{
		_openPrompt({ data: { message: message } }, on_confirm);
	};

	/**
	 * SET-43: the current indicator state, for a panel that has to restore something after a
	 * re-render because of it.
	 *
	 * READ-ONLY AND DELIBERATELY NARROW. The Flight Alert tab needs to know that the retroactive
	 * update half-failed so it can leave the control that triggers it on screen; everything else
	 * about the state machine stays private.
	 *
	 * @return {string}
	 */
	save.status = function statusPublic()
	{
		return status;
	};

	save.isDirty = function isDirtyPublic()
	{
		return _isDirty();
	};

	/**
	 * For a control that keeps its value somewhere this module cannot see.
	 *
	 * Ground rule 10 as S44 rewrote it: a non-native control must report WHAT IT NOW IS, not
	 * that it changed. The ordinary contract is to keep the current value in a form input and
	 * trigger change on it; this is the fallback for anything that cannot.
	 */
	save.recompute = function recomputePublic()
	{
		_recompute();
	};

	/**
	 * For a pane that BUILDS its own inputs when the panel renders.
	 *
	 * SET-18. The arrival snapshot is taken when my-settings:rendered fires, and a pane whose
	 * js writes hidden inputs after that point has changed the form without the advisor
	 * touching anything - so the next keystroke anywhere reads as "unsaved changes" and the
	 * leave guard claims edits that were never made. Specialties is the first pane like this:
	 * its three parallel arrays are drawn from the saved rows, so they ARE the arrival state.
	 *
	 * DIFFERENT FROM recompute(), which asks "am I dirty against arrival". This one says "this
	 * is arrival" - only correct for inputs the panel generated from stored values, never for
	 * anything the advisor changed.
	 */
	save.resetEntry = function resetEntry()
	{
		entry = _snapshot(_panelForm());

		_recompute();
	};

	/**
	 * Called by js/Adzuki/my_settings.js when a save came back successful.
	 */
	save.succeeded = function succeeded()
	{
		entry = null;

		// remembered per panel, so "All changes saved" is only claimed on the panel where a
		// save actually happened and navigating elsewhere goes back to saying nothing
		saved_url = ((Adzuki.MySettings && typeof Adzuki.MySettings.currentUrl === 'function')
			? Adzuki.MySettings.currentUrl()
			: null);

		_setStatus('saved');

		// if the advisor was on their way out when they chose Save and continue, finish the
		// journey now that the save has landed
		//
		// RETURNS WHETHER IT RESUMED, and the caller depends on it. my_settings.js re-renders the
		// current panel after every save to show the stored values - which, once this has already
		// navigated somewhere else, is a SECOND fetch racing the first. See SET-12 / 1 Oct 2026.
		if (deferred === null) {
			return false;
		}

		_resume();

		return true;
	};

	/**
	 * SET-43: called by js/Adzuki/my_settings.js when a save succeeded but a side effect of it
	 * did not fully apply.
	 *
	 * THE SAVE IS STILL A SAVE. entry is cleared and saved_url recorded exactly as on success, so
	 * the panel is not left looking dirty and the advisor is not invited to re-submit settings that
	 * are already stored. Only what the indicator says is different.
	 *
	 * IT DOES NOT TIME OUT. 'saved' fades back to rest after two seconds because it is a
	 * confirmation nobody needs to keep reading; this is a warning, and it stays until the next
	 * thing the advisor does.
	 *
	 * @param {string} message What to show. Falls back to a generic sentence when absent.
	 */
	save.partial = function partial(message)
	{
		entry = null;

		saved_url = ((Adzuki.MySettings && typeof Adzuki.MySettings.currentUrl === 'function')
			? Adzuki.MySettings.currentUrl()
			: null);

		partial_text = (message || null);

		_setStatus('partial');

		// returns whether it resumed, exactly as succeeded() does and for the same reason
		if (deferred === null) {
			return false;
		}

		_resume();

		return true;
	};

	$(document).ready(function documentReady() {
		// a fresh panel is a fresh entry state
		$(document).on('my-settings:rendered', function panelRendered() {
			entry = _snapshot(_panelForm());

			// SET-43: 'partial' SURVIVES A RENDER, like 'saved' and 'failed' before it.
			//
			// A save re-renders the panel from the server, and this runs after it. Left out of this
			// list, the warning was painted by save.partial() and then overwritten by 'All changes
			// saved' a few hundred milliseconds later - so the one state that had something to report
			// was the only one the advisor could not read.
			if (status !== 'saved' && status !== 'failed' && status !== 'partial') {
				status = _cleanState();
			}

			_paint();
			_recompute();
		});

		$(document).on('change input', '.my-settings-overlay form[data-form-component] ' + FIELDS, function fieldTouched() {
			_recompute();
		});

		// SET-136: A RICH-TEXT FIELD HAS TO SAY SO ITSELF.
		//
		// The selector above cannot see one. tinymce moves editing into an iframe and leaves the
		// <textarea> it replaced untouched until it syncs, so typing in an htmlEditor fires neither
		// `change` nor `input` on anything a selector can reach - and _comparable() above, which
		// already reads editor.getContent() correctly, was simply never asked.
		//
		// TWO THINGS WERE BROKEN BY THAT, and the quiet one is worse. Save never enabled, so a
		// panel whose only change was a message could not be saved at all; and the leave guard
		// never armed, so the advisor could switch tabs and lose the text with no prompt.
		//
		// THE ENTRY SNAPSHOT HAS TO BE RE-TAKEN, AND THAT IS THE HALF THAT IS EASY TO MISS.
		// tinymce.init() is ASYNCHRONOUS, so at my-settings:rendered the editors do not exist yet
		// and _snapshot() reads the raw textarea. Once they initialise, _comparable() starts
		// returning editor.getContent() - the same content NORMALISED, with tags closed and
		// entities rewritten - so the two never match and the panel opens claiming unsaved changes
		// it does not have. Re-snapshotting on each editor's own `init` is what makes the entry
		// state and the comparison speak the same language.
		//
		// AddEditor RATHER THAN WALKING tinymce.editors ON RENDER, because that walk races the
		// same asynchrony: an editor still being created is not in the list yet. The global event
		// fires per editor whenever it is created, which is exactly when there is something to
		// bind to.
		//
		// REGISTERED ONCE, AT READY. Binding it per render would stack a handler per panel view.
		//
		// SCOPED TO THE OVERLAY, because this fires for every editor in the application - the
		// signature builder and the email tools have their own, and they are none of this file's
		// business.
		//
		// EVENTS ARE IGNORED UNTIL THAT EDITOR SAYS IT IS READY. tinymce fires SetContent while it
		// is loading the initial value, which is not a change the advisor made.
		if (window.tinymce && typeof tinymce.on === 'function') {
			tinymce.on('AddEditor', function editorAdded(e) {
				var editor = e.editor,
				    ready  = false;

				if (!editor || typeof editor.on !== 'function') {
					return;
				}

				editor.on('init', function editorReady() {
					if ($(editor.getElement()).closest('.my-settings-overlay').length === 0) {
						return;
					}

					ready = true;

					// the entry state now includes this editor, read the way it will be read from
					// here on
					entry = _snapshot(_panelForm());

					_paint();
					_recompute();
				});

				// FIVE EVENTS, NOT JUST KeyUp. A bold click, a paste, an undo and a programmatic
				// insert all change the content without a keystroke reaching anything, and Change
				// alone misses typing until focus leaves - which would make Save arrive late rather
				// than not at all.
				editor.on('Change KeyUp SetContent Undo Redo', function editorTouched() {
					if (ready) {
						_recompute();
					}
				});
			});
		}

		// SET-74: "ALL CHANGES SAVED" IS A CLAIM ABOUT THIS MODAL SESSION, so closing the modal
		// has to forget it.
		//
		// saved_url was set on a successful save and never cleared, and panel_url comes back to
		// the same value when the same panel is reopened - so _cleanState() found them equal and
		// the indicator opened saying "All changes saved" on a panel where nothing had just been
		// saved. That is the exact claim the blank-on-arrival rule exists to prevent; it was
		// simply scoped to the page load rather than to the modal.
		//
		// entry and deferred go with it: the panel is gone, so entry state describes fields that
		// no longer exist, and a deferred navigation has nowhere left to go.
		$(document).on('my-settings:closed', function modalClosed() {
			saved_url = null;
			entry     = null;
			deferred  = null;

			// SET-58: an unanswered confirmation prompt goes with the panel it belonged to. Left
			// standing, its named bypass fields would be applied to whatever form opened next.
			_closePrompt();

			_setStatus('blank');
		});

		$(document).on('click', '.my-settings-save', function saveClicked(e) {
			e.preventDefault();

			save.submit();
		});

		// SET-76's three buttons
		// SET-72 QA: ABANDONING A DEFERRED ACTION IS NOW ANNOUNCED.
		//
		// Keep editing and the dialog backdrop both drop the deferred action and close, and said
		// nothing doing it - so anything holding state for the jump that never happened had no way
		// to know. The search dropdown held a highlight target and a cleared search box: the target
		// survived to glow at whatever the advisor opened next, and the term they had typed was
		// gone for a jump that was called off.
		$(document).on('click', '.my-settings-confirm-keep', function keepEditing(e) {
			e.preventDefault();

			deferred = null;

			_closeDialog();
			$(document).trigger('my-settings:navigation-cancelled');
		});

		$(document).on('click', '.my-settings-confirm-discard', function discardChanges(e) {
			e.preventDefault();

			_resume();
		});

		$(document).on('click', '.my-settings-confirm-save', function saveAndContinue(e) {
			e.preventDefault();

			// the deferred action runs when the save comes back successful, not now
			save.submit();
		});

		// the dialog's own backdrop behaves as Keep editing
		$(document).on('mousedown', '.my-settings-guard', function confirmBackdrop(e) {
			if (e.target === e.currentTarget) {
				deferred = null;

				_closeDialog();
				$(document).trigger('my-settings:navigation-cancelled');
			}
		});

		// SET-8 left exactly one seam for this: every dismissal path and every in-modal
		// navigation asks the guard, so the dialog is registered once here rather than
		// intercepted separately in three or four places.
		if (Adzuki.MySettings && typeof Adzuki.MySettings.registerGuard === 'function') {
			Adzuki.MySettings.registerGuard(function guard(action) {
				if (!_isDirty()) {
					return true;
				}

				_openDialog(action);

				return false;
			});
		}

		// SET-58's two prompt buttons
		$(document).on('click', '.my-settings-prompt-cancel', function promptCancel(e) {
			e.preventDefault();

			// nothing was saved and nothing is bypassed, so the next Save asks again
			_closePrompt();
		});

		$(document).on('click', '.my-settings-prompt-continue', function promptContinue(e) {
			var $form          = _panelForm(),
			    action         = prompt_action,
			    form_id_prefix = ($form.attr('name') || '').toLowerCase() + '-';

			e.preventDefault();

			// SET-60: an action endpoint raised this, so there is nothing to re-submit - run its
			// callback and stop. Read before _closePrompt() clears it.
			if (action !== null) {
				_closePrompt();

				action();

				return;
			}

			// set every field the prompt named, then submit again - the form sees the bypass and
			// commits instead of asking. The fields are declared inputs, so they ride the attribute
			// map like any other value and need no plumbing of their own.
			//
			// BY ID, NOT BY NAME, AND THAT WAS A REAL BUG. setConfirmationPromptFieldData() names
			// the FIELD KEY - 'confirmed_amex_tokenization' - but DefaultForm emits the input as
			// name="form_component_data[FormName][key]" (_parseNameAttribute). So the bare-name
			// lookup here matched nothing, the bypass was never set, and confirming re-ran a
			// confirm() that saw no bypass and prompted again: an unbreakable loop with no way to
			// save. Found on SET-131's push prompt; SET-58's AMEX prompt has the same shape and
			// the same defect.
			//
			// The id is the one thing _parseNameAttribute guarantees alongside the name -
			// strtolower(formName + '-' + key) - and it is what the per-form confirmation()
			// handlers in js/Adzuki/forms already use. The bare name is kept as a fallback for a
			// Default form, where the input really is named for its key.
			$.each(prompt_fields, function eachField(i, field) {
				var $field;

				if (!field || !field.name) {
					return;
				}

				$field = $form.find('#' + form_id_prefix + field.name.split('.').join('-'));

				if ($field.length === 0) {
					$field = $form.find('[name="' + field.name + '"]');
				}

				$field.val(field.value);
			});

			_closePrompt();

			save.submit();
		});

		// NO BACKDROP CANCEL ON THIS ONE, unlike the guard dialog above it.
		//
		// The guard asks about leaving a panel, and a stray click dismissing it costs nothing -
		// the advisor stays where they are. This dialog asks whether to commit something that
		// reaches outside the app: SET-131's push overwrites live public websites, and a form's
		// confirm() is by definition guarding a consequence its author thought worth naming. An
		// answer collected from a misplaced click is not an answer, so the two buttons are the
		// only way out.

		// SET-74's failure state, from form.js's own submit outcomes
		if (Adzuki.Form) {
			// SET-168: form.js has always passed the whole response as the third argument; it is
			// taken now because a form-level error carries the pane it belongs to.
			Adzuki.Form.onSubmitError = function submitFailed(form_element, error_status, response) {
				_setStatus('failed');

				// SET-123: a rejected field may be on a pane that is not showing
				if (error_status === 'validation_error') {
					_revealErrorPane(response);
				}

				// A failure while the discard dialog is open leaves the advisor looking at the
				// dialog with the inline errors hidden behind it - and the deferred action must
				// not run, because nothing was saved. Stand the dialog down so they can see what
				// went wrong and decide again.
				if (deferred !== null) {
					deferred = null;

					_closeDialog();
				}
			};

			// SET-58, decision S23. Returning false claims the response: form.js stops there and
			// leaves the dialog to this module. Scoped to forms inside the overlay, so a confirm()
			// anywhere else in the app keeps the framework's per-form path.
			Adzuki.Form.onConfirmationPrompt = function promptInModal(form_element, response) {
				var $overlay = $('.my-settings-overlay');

				if ($overlay.length === 0 || $.contains($overlay[0], form_element) === false) {
					return true;
				}

				_openPrompt(response);

				return false;
			};
		}
	});

	Adzuki.MySettings      = (Adzuki.MySettings || {});
	Adzuki.MySettings.Save = save;
}(jQuery, window.Adzuki || {}));
